import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Users, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw, TrendingUp, TrendingDown, Bell, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Activity, BarChart3, PieChart, LineChart, ScrollText, Database, HardDrive, Cpu, MemoryStick, Network, Server, Wrench, Shield, Archive, Download, Upload, List, Play, Pause, Stop, ArrowUp, ArrowDown, ArrowRight, ArrowLeft, RotateCcw, Settings, Target as TargetIcon, Zap as ZapIcon, Brain as BrainIcon, Route as RouteIcon, UserCheck, UserX, UserPlus, UserMinus, AlertTriangle, Clock as ClockIcon, Timer as TimerIcon, Calendar, ChevronUp, ChevronDown, ChevronRight, ChevronLeft, Star, ThumbsUp, ThumbsDown, MessageSquare as MessageSquareIcon, BookOpen, ClipboardList, CheckCircle, Circle, Square
} from 'lucide-react';
import { AgentService } from '../../services/AgentService';

const TicketResolution = () => {
  // Create service instance
  const agentService = useMemo(() => new AgentService(), []);
  
  const [resolutionMetrics, setResolutionMetrics] = useState(null);
  const [resolutionTemplates, setResolutionTemplates] = useState([]);
  const [resolutionQuality, setResolutionQuality] = useState(null);
  const [resolutionWorkflow, setResolutionWorkflow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [resolutionForm, setResolutionForm] = useState({
    ticketId: '',
    resolution: '',
    template: '',
    quality: '',
    notes: ''
  });

  const loadResolutionData = useCallback(async () => {
    try {
      setLoading(true);
      const [metricsResponse, templatesResponse, qualityResponse, workflowResponse] = await Promise.all([
        agentService.getResolutionMetrics(),
        agentService.getResolutionTemplates(),
        agentService.getResolutionQuality(),
        agentService.getResolutionWorkflow()
      ]);

      if (metricsResponse.success) setResolutionMetrics(metricsResponse.data);
      if (templatesResponse.success) setResolutionTemplates(templatesResponse.data);
      if (qualityResponse.success) setResolutionQuality(qualityResponse.data);
      if (workflowResponse.success) setResolutionWorkflow(workflowResponse.data);
      toast.success('Resolution data loaded successfully');
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load resolution data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [agentService]);

  useEffect(() => {
    loadResolutionData();
  }, [loadResolutionData]);

  const handleCreateResolution = async () => {
    try {
      const response = await agentService.createResolution(resolutionForm);
      if (response.success) {
        toast.success('Resolution created successfully!');
        await loadResolutionData();
        setResolutionForm({
          ticketId: '',
          resolution: '',
          template: '',
          quality: '',
          notes: ''
        });
      } else {
        toast.error('Failed to create resolution');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Error creating resolution: ' + err.message);
    }
  };

  const handleUseTemplate = (template) => {
    setResolutionForm({
      ...resolutionForm,
      template: template.id,
      resolution: template.content
    });
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'fair': return 'bg-yellow-100 text-yellow-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getQualityIcon = (quality) => {
    switch (quality) {
      case 'excellent': return <Star className="w-4 h-4 text-green-600" />;
      case 'good': return <ThumbsUp className="w-4 h-4 text-blue-600" />;
      case 'fair': return <Circle className="w-4 h-4 text-yellow-600" />;
      case 'poor': return <ThumbsDown className="w-4 h-4 text-red-600" />;
      default: return <Circle className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading resolution data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ticket Resolution Tracking</h1>
          <p className="text-gray-600">Track and improve ticket resolution quality</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadResolutionData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: LayoutDashboard },
            { id: 'templates', name: 'Resolution Templates', icon: FileText },
            { id: 'quality', name: 'Quality Metrics', icon: BarChart3 },
            { id: 'workflow', name: 'Resolution Workflow', icon: Route }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && resolutionMetrics && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resolutionMetrics.overviewMetrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'good' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'good' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Resolution Quality Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Resolution Quality Distribution</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {resolutionMetrics.qualityDistribution.map((quality, index) => (
                  <div key={index} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      quality.level === 'excellent' ? 'bg-green-100' :
                      quality.level === 'good' ? 'bg-blue-100' :
                      quality.level === 'fair' ? 'bg-yellow-100' :
                      'bg-red-100'
                    }`}>
                      {getQualityIcon(quality.level)}
                    </div>
                    <h4 className="text-lg font-medium text-gray-900">{quality.level}</h4>
                    <p className="text-sm text-gray-600">{quality.count} resolutions</p>
                    <p className={`text-sm font-medium mt-1 ${
                      quality.level === 'excellent' ? 'text-green-600' :
                      quality.level === 'good' ? 'text-blue-600' :
                      quality.level === 'fair' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {quality.percentage}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Resolutions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Resolutions</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {resolutionMetrics.recentResolutions.map((resolution, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getQualityIcon(resolution.quality)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Ticket #{resolution.ticketId}</p>
                        <p className="text-sm text-gray-600">{resolution.resolution}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{resolution.timestamp}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQualityColor(resolution.quality)}`}>
                        {resolution.quality}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolution Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Templates List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Resolution Templates</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resolutionTemplates.map((template) => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.category === 'technical' ? 'bg-blue-100 text-blue-800' :
                        template.category === 'account' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {template.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">{template.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Used {template.usageCount} times</span>
                      <button
                        onClick={() => handleUseTemplate(template)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                      >
                        Use Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quality Metrics Tab */}
      {activeTab === 'quality' && resolutionQuality && (
        <div className="space-y-6">
          {/* Quality Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resolutionQuality.metrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'good' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'good' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        metric.status === 'good' ? 'bg-green-500' :
                        metric.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${metric.percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{metric.percentage}% of target</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolution Workflow Tab */}
      {activeTab === 'workflow' && (
        <div className="space-y-6">
          {/* Workflow Steps */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Resolution Workflow</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {resolutionWorkflow.map((step, index) => (
                  <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-4 ${
                      step.completed ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {step.completed ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{step.name}</h4>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        step.status === 'completed' ? 'bg-green-100 text-green-800' :
                        step.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {step.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketResolution;
